import { QUESTIONNAIRE_PHOTO_MAX_BYTES } from "@ferrivia/api-contract";

const LONG_EDGE_PX = 2048;
const JPEG_QUALITY = 0.85;

export type PhotoPrepFailure = "not_image" | "too_large";

export class PhotoPrepError extends Error {
  constructor(readonly reason: PhotoPrepFailure) {
    super(reason);
  }
}

/* Re-encodes a picked file to a JPEG of at most 2048 px on the long edge
   before it leaves the device. Drawing to a canvas and exporting drops all
   EXIF (including GPS) and turns HEIC into JPEG on browsers that can decode
   it; `imageOrientation: "from-image"` bakes the EXIF rotation into the
   pixels first so the photo isn't sideways once the tag is gone. The image
   is never inspected beyond its dimensions. */
export async function preparePhoto(file: File): Promise<Blob> {
  const bitmap = await decode(file);

  try {
    const scale = Math.min(1, LONG_EDGE_PX / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new PhotoPrepError("not_image");
    context.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
    if (!blob) throw new PhotoPrepError("not_image");
    if (blob.size > QUESTIONNAIRE_PHOTO_MAX_BYTES) throw new PhotoPrepError("too_large");
    return blob;
  } finally {
    bitmap.close();
  }
}

const HEIC_TYPES = new Set(["image/heic", "image/heif", "image/heic-sequence", "image/heif-sequence"]);

function looksLikeHeic(file: File): boolean {
  return HEIC_TYPES.has(file.type.toLowerCase()) || /\.(heic|heif)$/i.test(file.name);
}

/* The browser's own decoder first: iOS usually hands a picked photo over as
   JPEG, and Safari decodes HEIC natively. Desktop Chrome, Firefox and Edge
   can't decode HEIC at all, so a HEIC file that fails falls back to libheif
   (heic-to, LGPL-3.0), loaded as its own chunk only when needed. Still
   decoded on the device: the server only ever sees our re-encoded JPEG. */
async function decode(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    // fall through to the HEIC decoder
  }
  if (!looksLikeHeic(file)) {
    throw new PhotoPrepError("not_image");
  }
  try {
    const { heicTo } = await import("heic-to/csp");
    return await heicTo({ blob: file, type: "bitmap" });
  } catch {
    throw new PhotoPrepError("not_image");
  }
}
